/**
 * Loading Screen
 * Shows loading animation while system initializes
 */

import { useEffect, useState } from 'react';
import { useScreenNavigation } from '../../hooks';
import { documentVerificationService } from '../../services/DocumentVerificationService';
import { sessionStateManager } from '../../utils/SessionStateManager';
import { VerificationScreenTemplate, VerticalStack } from '../shared';
import { designTokens } from '../../constants/designTokens';
import { tUnsafe, type TranslationKey, useLanguage } from '../../i18n';

export function LoadingScreen() {
  const { navigateToScreen } = useScreenNavigation();
  const { t } = useLanguage();
  const [statusKey, setStatusKey] = useState<TranslationKey>(
    'loading.status.initializing'
  );

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let hasNavigated = false;

    const initializeVerification = async () => {
      try {
        // The API key is populated from authenticated session configuration.
        const sessionState = sessionStateManager.getState();
        const apiKey = sessionState?.config?.apiKey;

        if (!apiKey) {
          console.error('[SafePassage] No API key configured');
          navigateToScreen('failure', {
            errorMessage: tUnsafe('loading.errors.noApiKey'),
          });
          hasNavigated = true;
          return;
        }

        setStatusKey('loading.status.settingUpApiKey');

        // Set API key for document verification service (may already be set)
        documentVerificationService.setApiKey(apiKey);

        // Skip camera permission check temporarily for debugging
        setStatusKey('loading.status.ready');

        // Navigate to camera-access after a short delay - only if not already navigated
        timeoutId = setTimeout(() => {
          if (!hasNavigated) {
            hasNavigated = true;
            try {
              navigateToScreen('camera-access');
            } catch {
              // Navigation error - silently handle
            }
          }
        }, 2000);
      } catch {
        setStatusKey('loading.status.errorRetrying');
        if (!hasNavigated) {
          timeoutId = setTimeout(() => {
            if (!hasNavigated) {
              hasNavigated = true;
              navigateToScreen('camera-access');
            }
          }, 1000);
        }
      }
    };

    initializeVerification();

    // Cleanup timeout on unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [navigateToScreen]);

  return (
    <VerificationScreenTemplate
      title={t('loading.title')}
      description={t('loading.description')}
      showLogo={false}
      showBackButton={false}
      showProgress={false}
      desktopOptimized={true}
    >
      <VerticalStack spacing="2xl">
        {/* Security Shield Icon - Fixed 275px width like ID Request */}
        <div className="shield-container">
          <div className="shield-icon">
            <svg
              width="275"
              height="275"
              viewBox="0 0 275 275"
              fill="none"
              className="shield-svg"
            >
              <path
                d="M137.5 34.375L206.25 68.75V120.3125C206.25 171.875 178.75 213.125 137.5 233.75C96.25 213.125 68.75 171.875 68.75 120.3125V68.75L137.5 34.375Z"
                fill="url(#shieldGradient)"
                stroke={designTokens.colors.brand.primary}
                strokeWidth="4"
              />
              <defs>
                <linearGradient
                  id="shieldGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor={designTokens.colors.brand.primaryHover} />
                  <stop
                    offset="100%"
                    stopColor={designTokens.colors.brand.primary}
                  />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        {/* Progress Section */}
        <div className="progress-section">
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
          <div className="progress-text">{t(statusKey)}</div>
        </div>
      </VerticalStack>

      <style>{`
        .shield-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .shield-icon {
          width: 275px;
          height: 275px;
          animation: pulse 2s ease-in-out infinite;
        }
        
        .shield-svg {
          width: 100%;
          height: 100%;
        }

        @keyframes pulse {
          0%, 100% { 
            opacity: 1;
            transform: scale(1);
          }
          50% { 
            opacity: 0.8;
            transform: scale(1.05);
          }
        }

        .progress-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: ${designTokens.spacing.md};
          width: 100%;
          max-width: 200px;
          margin: 0 auto;
        }

        .progress-bar {
          width: 100%;
          height: 4px;
          background-color: ${designTokens.colors.neutral.border};
          border-radius: ${designTokens.borderRadius.xs};
          overflow: hidden;
        }

        .progress-fill {
          width: 25%;
          height: 100%;
          background: linear-gradient(90deg, ${designTokens.colors.brand.primary}, ${designTokens.colors.brand.primaryHover});
          border-radius: ${designTokens.borderRadius.xs};
          animation: progressPulse 2s ease-in-out infinite;
        }

        @keyframes progressPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        .progress-text {
          font-size: ${designTokens.typography.fontSize.sm};
          font-weight: 600;
          color: ${designTokens.colors.brand.primary};
        }

        /* Desktop enhancements - Phase 6 */
        ${designTokens.media.desktop} {
          .progress-section {
            max-width: 280px;
          }
          
          .progress-bar {
            height: 6px;
          }
          
          .progress-text {
            font-size: ${designTokens.typography.fontSize.base};
          }
        }
      `}</style>
    </VerificationScreenTemplate>
  );
}
